export function cleanString(str: string | null | undefined): string {
  return (str || '')
    .replace(/[^a-zA-Z0-9]/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .trim();
}

export function currencyFormatter(amount: number = 0, minFractionDigits = 2): string {
  const currency = new Intl.NumberFormat('en-AU', { 
    style: 'currency', 
    currency: 'AUD', 
    minimumFractionDigits: minFractionDigits 
  });
  return currency.format(amount);
}

export function percentageFormatter(amount: number = 0): string {
  const currency = new Intl.NumberFormat('en-AU', { 
    style: 'percent', 
    minimumFractionDigits: 2 
  });
  return currency.format(amount);
}

export function toTitleCase(str: string = '') {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());
}

export function sanitizeNumberInput(value: string): number {
    if (!value) return 0;

    let sanitized = value.replace(/[^0-9.]/g, '');
    const parts = sanitized.split('.');
  
    if (parts.length > 2) {
        sanitized = parts[0] + '.' + parts.slice(1).join('');
    }

    const parsed = parseFloat(sanitized);
    return isNaN(parsed) ? 0 : parsed;
}

export function sanitizeEmailInput(value: string): string {
    if (!value) return '';
    let sanitized = value.trim().replace(/\s+/g, '');
    sanitized = sanitized.replace(/[^a-zA-Z0-9@._+-]/g, '');
    return sanitized;
}

export function sanitizeAuPhoneInput(value: string, isFormat: boolean = false): string {
    if (!value) return '';

    let sanitized = value.replace(/[^\d+]/g, '');
    if (sanitized.indexOf('+') > 0) {
        sanitized = sanitized.replace(/\+/g, (match, offset) => offset === 0 ? '+' : '');
    }

    if (sanitized.startsWith('+61')) {
        if (sanitized.length > 12) {
            sanitized = sanitized.slice(0, 12);
        }
    } else {
        if (sanitized.length > 10) {
            sanitized = sanitized.slice(0, 10);
        }
    }

    if (!isFormat) {
        return sanitized;
    }

    let digits = sanitized.replace(/^\+?61/, '0');

    if (/^0?4\d*$/.test(digits)) {
        const cleanDigits = digits.startsWith('0') ? digits : '0' + digits;
        if (cleanDigits.length > 7) {
            return `${cleanDigits.slice(0, 4)} ${cleanDigits.slice(4, 7)} ${cleanDigits.slice(7, 10)}`;
        } else if (cleanDigits.length > 4) {
            return `${cleanDigits.slice(0, 4)} ${cleanDigits.slice(4)}`;
        }
        return cleanDigits;
    }

    if (digits.length > 6) {
        return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
    } else if (digits.length > 3) {
        return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    }

    return sanitized;
}

export function getInitials (value: string) {
    if (!value?.trim()) return "";

    const words = value.trim().split(/\s+/);
    if (words.length === 1) {
        return words[0].charAt(0).toUpperCase();
    }

    const firstInitial = words[0].charAt(0);
    const lastInitial = words[words.length - 1].charAt(0);

    return (firstInitial + lastInitial).toUpperCase();
}

/** "https://www.Custm.com.au/contact" -> "custm.com.au" - how a Company Domain Name is kept. Null when empty. */
export function toCompanyDomain(value: string | null | undefined): string | null {
  const domain = String(value ?? '').trim().toLowerCase()
    .replace(/^[a-z]+:\/\//, '').replace(/^www\./, '').replace(/[/?#].*$/, '');
  return domain || null;
}

export function secondsToTimeDurations (seconds: number = 0, isFormat: boolean = true) {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (!isFormat) {
        return {
            days,
            hours,
            minutes
        };
    }

    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0 || days > 0) parts.push(`${hours}h`);
    parts.push(`${minutes}m`);

    return parts.join(' ');
}

export function getTimeAgo(timestamp: string | Date | number): string {
  if (!timestamp) return '';

  const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
  if (seconds < 60) return 'just now';

  const intervals: [number, string][] = [
    [31536000, 'y'],
    [2592000, 'mo'],
    [604800, 'wk'],
    [86400, 'd'],
    [3600, 'h'],
    [60, 'm'],
  ];

  for (const [sec, unit] of intervals) {
    const count = Math.floor(seconds / sec);
    if (count >= 1) return `${count}${unit} ago`;
  }

  return 'just now';
}